import { useMemo, useState } from "react"
import type { Rubrica } from "@/types/analise-previdenciario"
import {
  formatDateTime,
  getNatRubrLabel,
  getNatRubrColor,
} from "../../../utils/previdenciario-utils"
import { CopyableText } from "./copyable-text"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Search } from "lucide-react"

interface RubricasTableProps {
  rubricas: Rubrica[]
}

export function RubricasTable({ rubricas }: RubricasTableProps) {
  const [search, setSearch] = useState("")
  const [natFilter, setNatFilter] = useState<string>("all")

  // Get unique naturezas for filter
  const uniqueNaturezas = useMemo(() => {
    const nats = new Set(rubricas.map((r) => r.natRubr))
    return Array.from(nats).sort()
  }, [rubricas])

  // Get unique codRubr for stats
  const uniqueCodes = useMemo(() => {
    return new Set(rubricas.map((r) => r.codRubr)).size
  }, [rubricas])

  const filtered = useMemo(() => {
    let result = rubricas

    if (search) {
      const term = search.toLowerCase()
      result = result.filter(
        (r) =>
          r.codRubr.toLowerCase().includes(term) ||
          r.dscrRubrica.toLowerCase().includes(term) ||
          r.nrRecibo.toLowerCase().includes(term) ||
          r.ideDmDev.toLowerCase().includes(term)
      )
    }

    if (natFilter !== "all") {
      result = result.filter((r) => r.natRubr === natFilter)
    }

    return result
  }, [rubricas, search, natFilter])

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <div className="rounded-lg border bg-card p-3">
          <p className="text-[10px] text-muted-foreground uppercase">
            Total de Rubricas
          </p>
          <p className="text-lg font-bold">{rubricas.length}</p>
        </div>
        <div className="rounded-lg border bg-card p-3">
          <p className="text-[10px] text-muted-foreground uppercase">
            Codigos Unicos
          </p>
          <p className="text-lg font-bold">{uniqueCodes}</p>
        </div>
        <div className="rounded-lg border bg-card p-3">
          <p className="text-[10px] text-muted-foreground uppercase">
            Naturezas
          </p>
          <p className="text-lg font-bold">{uniqueNaturezas.length}</p>
        </div>
        <div className="rounded-lg border bg-card p-3">
          <p className="text-[10px] text-muted-foreground uppercase">
            Filtrados
          </p>
          <p className="text-lg font-bold">{filtered.length}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por codigo, descricao, recibo..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 text-sm"
          />
        </div>
        <Select value={natFilter} onValueChange={setNatFilter}>
          <SelectTrigger className="w-56">
            <SelectValue placeholder="Natureza" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as naturezas</SelectItem>
            {uniqueNaturezas.map((nat) => (
              <SelectItem key={nat} value={nat}>
                {getNatRubrLabel(nat)} ({nat})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="rounded-lg border overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="text-xs">Cod Rubrica</TableHead>
                <TableHead className="text-xs">Descricao</TableHead>
                <TableHead className="text-xs">Natureza</TableHead>
                <TableHead className="text-xs">Tab</TableHead>
                <TableHead className="text-xs min-w-[260px]">Nr Recibo</TableHead>
                <TableHead className="text-xs">ideDmDev</TableHead>
                <TableHead className="text-xs">Processamento</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    className="text-center text-sm text-muted-foreground py-8"
                  >
                    Nenhuma rubrica encontrada
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((r, i) => (
                  <TableRow key={`${r.codRubr}-${r.nrRecibo}-${i}`}>
                    <TableCell className="font-mono text-xs font-semibold">
                      {r.codRubr}
                    </TableCell>
                    <TableCell className="text-xs max-w-[200px]">
                      {r.dscrRubrica}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="secondary"
                        className={`text-[10px] ${getNatRubrColor(r.natRubr)}`}
                      >
                        {getNatRubrLabel(r.natRubr)}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {r.ideTabRubr}
                    </TableCell>
                    <TableCell>
                      <CopyableText text={r.nrRecibo} className="text-[11px]" />
                    </TableCell>
                    <TableCell>
                      <CopyableText
                        text={r.ideDmDev}
                        className="text-[11px]"
                      />
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                      {formatDateTime(r.dhProcessamento)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  )
}
